import React from "react";

import {
  SHOWCASE_FADE_IN_MS,
  SHOWCASE_HOLD_MS,
  SHOWCASE_MS,
} from "../constants";
import { marcImageUrl } from "../paths";

// Owns the marc-completion showcase + end-screen-recap data.
//
// Showcase fires AFTER EACH MARC in a speedrun round (not just at round
// end) — it's the inter-marc punchline beat ("you made him") + the
// cross-fade choreography that swaps marcs under cover of opacity 0 +
// the per-marc data capture for the end-screen recap.
//
// When the per-marc engine reports allDone, triggerShowcase fires,
// captures snapshots, and runs two timers across SHOWCASE_MS:
//
//   advance — at FADE_IN+HOLD. Calls advanceMarc, which swaps the
//             easel src + starts the egg tween. The easel is at
//             opacity 0 at this instant (its keyframes is at the hold
//             end), so the src swap is invisible; phase 3 then fades
//             the new marc in.
//   end     — at SHOWCASE_MS. Clears showcasing, which unmounts the
//             canvas marc overlay and re-enables input (modulo
//             eggTweening, which gates input through the rest of the
//             egg tween if it outlasts SHOWCASE_FADE_OUT_MS).
//
// Snapshots captured at trigger time:
//
//   showcaseMarc      — { src, marcTransform } (or null). Frozen so
//                       the canvas overlay keeps showing the JUST-
//                       completed marc through phase 3 even after
//                       advanceMarc has flipped effectiveMarcId. Gated
//                       on `active && showcasing` so JSX consumers get
//                       a single named value, not a 3-condition
//                       expression.
//   roundDrawings     — per-marc record { drawingSrc, strokes,
//                       palette, eggTransform, marcTransform,
//                       finishedAt, duration }. Canvas is cleared
//                       between marcs, so the snapshot has to happen
//                       before advance. drawingSrc (PNG via
//                       toDataURL) is what the end-screen modal
//                       renders; strokes/palette are kept around for
//                       a future gallery flow that re-rasterizes at
//                       different sizes.
//
// The end-screen modal sits on top of this state: it opens when
// speedrun.complete AND !showcasing — i.e., after the LAST marc's
// showcase has finished playing through. The debug `n` hotkey fakes a
// completed round via triggerDebugEndScreen, which populates
// roundDrawings with three random reference traces and forces the
// modal open without any real play. All dismissal paths (real or
// debug) go via setMode(LANDING), which triggers the active-flips-
// false reset below to clear roundDrawings + debugEndScreenOpen.
export default function useShowcase({
  active,
  allDone,
  advanceMarc,
  effectiveMarcId,
  eff,
  strokes,
  canvasRef,
  speedrunLineup,
  speedrunStartedAt,
  onMarcCaptured,
}) {
  const [showcasing, setShowcasing] = React.useState(false);
  const [showcaseMarcSnapshot, setShowcaseMarcSnapshot] = React.useState(null);
  const [roundDrawings, setRoundDrawings] = React.useState({});
  const [debugEndScreenOpen, setDebugEndScreenOpen] = React.useState(false);

  // Synchronous mirror of roundDrawings. Triggershowcase needs the
  // previous marc's finishedAt to compute the current marc's duration,
  // and it needs to read it on the same tick it writes the new entry —
  // setRoundDrawings updaters don't run inline when the fiber has
  // pending lanes (e.g., the setShowcaseMarcSnapshot above dirties
  // them), so reading state-via-prev-in-updater can't be trusted to
  // run before our synchronous onMarcCaptured call.
  const roundDrawingsRef = React.useRef({});

  const showcaseAdvanceTimerRef = React.useRef(null);
  const showcaseEndTimerRef = React.useRef(null);

  // Showcase trigger as a callback so both the natural allDone effect
  // and the debug `v` hotkey can fire it. Self-guards against re-entry
  // while a showcase is already in flight.
  const effMarcTransform = eff.marcTransform;
  const effPalette = eff.palette;
  const effEggTransform = eff.eggTransform;
  const effEggLights = eff.eggLights;
  const effEggWarmth = eff.eggWarmth;
  const effEggRedshift = eff.eggRedshift;
  const triggerShowcase = React.useCallback(() => {
    if (
      showcaseAdvanceTimerRef.current != null ||
      showcaseEndTimerRef.current != null
    )
      return;
    setShowcaseMarcSnapshot({
      src: marcImageUrl(effectiveMarcId),
      marcTransform: effMarcTransform,
    });
    // toDataURL reads the canvas synchronously — must run BEFORE the
    // marcId-keyed drawing.clear() effect fires (which it will, in
    // the tail of this render's commit, when advanceMarc is called
    // below). Capturing right here is safely "as of allDone time."
    const drawingSrc = canvasRef.current?.toDataURL("image/png") ?? null;
    const finishedAt = Date.now();
    // Per-marc duration: time from when the player STARTED this
    // marc to when they finished. For marc N>0 the start is the
    // previous marc's finishedAt; for the first marc it's the
    // round-level startedAt (markStarted timestamp).
    const idx = speedrunLineup.indexOf(effectiveMarcId);
    const prevMarcId = idx > 0 ? speedrunLineup[idx - 1] : null;
    const prevFinishedAt = prevMarcId
      ? (roundDrawingsRef.current[prevMarcId]?.finishedAt ?? null)
      : speedrunStartedAt;
    const duration =
      prevFinishedAt != null ? finishedAt - prevFinishedAt : null;
    const captured = {
      drawingSrc,
      strokes,
      palette: effPalette,
      eggTransform: effEggTransform,
      // Saved so the end-screen can reposition the marc photo
      // identically to play — guarantees marc's head sits where
      // the egg's head sits in the drawn portrait next to it.
      marcTransform: effMarcTransform,
      // Lights/warmth/redshift ride along so the per-marc Download
      // button on the end-screen can render a composite that matches
      // what the player saw, not the canonical reference defaults.
      eggLights: effEggLights,
      eggWarmth: effEggWarmth,
      eggRedshift: effEggRedshift,
      finishedAt,
      duration,
    };
    const next = { ...roundDrawingsRef.current, [effectiveMarcId]: captured };
    roundDrawingsRef.current = next;
    setRoundDrawings(next);
    if (onMarcCaptured) onMarcCaptured(effectiveMarcId, captured);
    setShowcasing(true);
    showcaseAdvanceTimerRef.current = setTimeout(() => {
      showcaseAdvanceTimerRef.current = null;
      advanceMarc();
    }, SHOWCASE_FADE_IN_MS + SHOWCASE_HOLD_MS);
    showcaseEndTimerRef.current = setTimeout(() => {
      showcaseEndTimerRef.current = null;
      setShowcasing(false);
      setShowcaseMarcSnapshot(null);
    }, SHOWCASE_MS);
  }, [
    advanceMarc,
    effectiveMarcId,
    effMarcTransform,
    effPalette,
    effEggTransform,
    strokes,
    canvasRef,
    speedrunLineup,
    speedrunStartedAt,
    onMarcCaptured,
  ]);

  // Auto-fire on allDone while active. Depends on the stable
  // advanceMarc callback (not the speedrun object) — the object's
  // identity changes every render, which double-fired this effect
  // and skipped a marc per round.
  React.useEffect(() => {
    if (!active) return;
    if (!allDone) return;
    triggerShowcase();
  }, [active, allDone, triggerShowcase]);

  // Reset on inactive (mode flips out of speedrun mid-showcase, e.g.,
  // back to landing). Clears both pending timers so the easel/canvas
  // don't stay stuck mid-fade and we don't fire a stale advanceMarc.
  // Also drops captured per-marc drawings + the debug end-screen flag
  // — end-screen state is round-scoped, leaving speedrun resets it.
  React.useEffect(() => {
    if (active) return;
    if (showcaseAdvanceTimerRef.current != null) {
      clearTimeout(showcaseAdvanceTimerRef.current);
      showcaseAdvanceTimerRef.current = null;
    }
    if (showcaseEndTimerRef.current != null) {
      clearTimeout(showcaseEndTimerRef.current);
      showcaseEndTimerRef.current = null;
    }
    setShowcasing(false);
    setShowcaseMarcSnapshot(null);
    roundDrawingsRef.current = {};
    setRoundDrawings({});
    setDebugEndScreenOpen(false);
  }, [active]);

  // Debug-only: `n` hotkey populates fake roundDrawings and forces the
  // end-screen open so we can iterate on the modal without playing
  // through. Strokes/palette aren't populated (no real play); modal
  // renders from drawingSrc only.
  const triggerDebugEndScreen = React.useCallback((fakeDrawings) => {
    roundDrawingsRef.current = fakeDrawings;
    setRoundDrawings(fakeDrawings);
    setDebugEndScreenOpen(true);
  }, []);

  // Merged showcaseMarc — JSX gets a finished {src, marcTransform} or
  // null, instead of having to gate on (active && showcasing && snapshot).
  const showcaseMarc =
    active && showcasing && showcaseMarcSnapshot ? showcaseMarcSnapshot : null;

  return {
    showcasing,
    showcaseMarc,
    roundDrawings,
    triggerShowcase,
    debugEndScreenOpen,
    triggerDebugEndScreen,
  };
}
